import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CaseStudyHero from "../components/CaseStudyHero";
import CaseStudySection from "../components/CaseStudySection";
import CaseStudyResults from "../components/CaseStudyResults";
import CaseStudyTestimonial from "../components/CaseStudyTestimonial";
import SEOHead from "../components/SEOHead";
import { CASE_STUDIES, getCaseStudyBySlug } from "../data/caseStudies";
import { ArrowRight, ArrowLeft, Check, X } from "lucide-react";

import { useTranslation } from 'react-i18next';

function MarkdownContent({ content }) {
  if (!content) return null;

  const paragraphs = content.split("\n\n").filter(Boolean);

  return (
    <div className="space-y-6">
      {paragraphs.map((para, i) => {
        const parts = para.split(/(\*\*[^*]+\*\*)/g);
        return (
          <p key={i} className="text-zinc-400 text-base leading-relaxed">
            {parts.map((part, j) => {
              if (part.startsWith("**") && part.endsWith("**")) {
                return (
                  <strong key={j} className="text-white font-semibold">
                    {part.slice(2, -2)}
                  </strong>
                );
              }
              return part;
            })}
          </p>
        );
      })}
    </div>
  );
}

function ImplementationSection({ implementation }) {
  const { t } = useTranslation();
  if (!implementation) return null;

  return (
    <section className="border-t border-zinc-900">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 md:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">
              // 03 / IMPLEMENTATION
            </div>
            <h2 className="font-display text-3xl md:text-4xl uppercase leading-tight">
              How we<br /><span className="text-amber-500">did it.</span>
            </h2>
            <div className="mt-6 flex items-center gap-3">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{t('case_study.timeline')}</span>
              <span className="font-display text-lg text-amber-500">
                {implementation.timeline}
              </span>
            </div>
            {implementation.channels && (
              <div className="mt-4">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 block mb-2">{t('case_study.channels')}</span>
                <div className="flex flex-wrap gap-2">
                  {implementation.channels.map((ch) => (
                    <span
                      key={ch}
                      className="font-mono text-[10px] uppercase tracking-[0.15em] text-amber-500 border border-amber-500/30 px-2 py-1"
                    >
                      {ch}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6">
            <div className="space-y-0">
              {implementation.steps.map((step, i) => (
                <div key={i} className="relative flex gap-6 group">
                  {/* Timeline connector */}
                  {i < implementation.steps.length - 1 && (
                    <div className="absolute left-[4.5rem] top-8 w-[1px] h-full bg-zinc-800 group-hover:bg-zinc-700 transition-colors" />
                  )}

                  <div className="flex-shrink-0 w-20 pt-1 relative">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 relative z-10">
                      {step.week}
                    </span>
                    {/* Timeline dot */}
                    <div className="absolute right-0 top-1.5 w-2 h-2 bg-amber-500 rounded-full translate-x-1/2" />
                  </div>
                  <div className="flex-grow border-l border-zinc-800 pl-6 pb-8 group-hover:border-zinc-700 transition-colors">
                    <h3 className="font-display text-lg uppercase text-white mb-2 group-hover:text-amber-500 transition-colors">
                      {step.title}
                    </h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BeforeAfterSection({ beforeAfter }) {
  const { t } = useTranslation();
  if (!beforeAfter) return null;

  return (
    <section className="border-t border-zinc-900 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">
          // BEFORE & AFTER
        </div>
        <h2 className="font-display text-3xl md:text-4xl uppercase leading-tight mb-4">
          The<br /><span className="text-amber-500">transformation.</span>
        </h2>
        <p className="text-zinc-400 text-base max-w-2xl mb-12">
          A side-by-side comparison of the key metrics before and after Max OS Cloud.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Before */}
          <div className="relative border border-zinc-800 p-8 group hover:border-red-500/30 transition-all duration-300">
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-red-500/50 to-transparent" />
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                <X className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="font-display text-xl uppercase text-zinc-400">{t('case_study.before')}</h3>
            </div>
            <ul className="space-y-4">
              {beforeAfter.before.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <X className="w-4 h-4 text-red-500/60 flex-shrink-0 mt-1" />
                  <span className="text-zinc-400 text-sm leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* After */}
          <div className="relative border border-amber-500/30 p-8 bg-amber-500/5 group hover:border-amber-500/50 transition-all duration-300">
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-amber-500/50 to-transparent" />
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                <Check className="w-5 h-5 text-amber-500" />
              </div>
              <h3 className="font-display text-xl uppercase text-amber-500">{t('case_study.after')}</h3>
            </div>
            <ul className="space-y-4">
              {beforeAfter.after.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" />
                  <span className="text-zinc-200 text-sm leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function CaseNavigation({ currentSlug }) {
  const { t } = useTranslation();
  const currentIndex = CASE_STUDIES.findIndex((cs) => cs.slug === currentSlug);
  const prevCase = currentIndex > 0 ? CASE_STUDIES[currentIndex - 1] : null;
  const nextCase =
    currentIndex < CASE_STUDIES.length - 1
      ? CASE_STUDIES[currentIndex + 1]
      : null;

  if (!prevCase && !nextCase) return null;

  return (
    <section className="border-t border-zinc-900">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {prevCase ? (
            <Link
              to={`/cases/${prevCase.slug}`}
              className="group flex items-center gap-4 border border-zinc-800 hover:border-amber-500/30 p-6 transition-all duration-300"
            >
              <ArrowLeft className="w-5 h-5 text-zinc-500 group-hover:text-amber-500 transition-colors flex-shrink-0" />
              <div>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-1">{t('case_study.previous_case')}</span>
                <span className="font-display text-sm uppercase text-white group-hover:text-amber-500 transition-colors">
                  {prevCase.company}
                </span>
              </div>
            </Link>
          ) : (
            <div />
          )}
          {nextCase ? (
            <Link
              to={`/cases/${nextCase.slug}`}
              className="group flex items-center justify-end gap-4 border border-zinc-800 hover:border-amber-500/30 p-6 transition-all duration-300 text-right"
            >
              <div>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-1">{t('case_study.next_case')}</span>
                <span className="font-display text-sm uppercase text-white group-hover:text-amber-500 transition-colors">
                  {nextCase.company}
                </span>
              </div>
              <ArrowRight className="w-5 h-5 text-zinc-500 group-hover:text-amber-500 transition-colors flex-shrink-0" />
            </Link>
          ) : (
            <div />
          )}
        </div>
      </div>
    </section>
  );
}

export default function CaseStudy() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const cs = getCaseStudyBySlug(slug);

  if (!cs) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-32 text-center">
          <h1 className="font-display text-4xl uppercase text-zinc-600 mb-4">{t('case_study.case_study_not_found')}</h1>
          <Link
            to="/cases"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-amber-500"
          >
            View all case studies <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      {cs && (
        <SEOHead
          title={`${cs.company} — Max Employee Case Study`}
          description={cs.excerpt}
          url={`/cases/${slug}`}
          structuredData={[
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
                { "@type": "ListItem", position: 2, name: "Case Studies", item: "https://maxemployee.com/cases" },
                { "@type": "ListItem", position: 3, name: cs.company, item: `https://maxemployee.com/cases/${slug}` },
              ],
            },
          ]}
        />
      )}
      <Header />

      <CaseStudyHero
        company={cs.company}
        industry={cs.industry}
        headline={cs.headline}
        results={cs.results}
      />

      {/* Challenge */}
      <div className="border-t border-zinc-900">
        <CaseStudySection
          title={t('case_study.the_challenge')}
          label="// 01 / THE CHALLENGE"
        >
          <MarkdownContent content={cs.challenge} />
        </CaseStudySection>
      </div>

      {/* Solution */}
      <div className="border-t border-zinc-900">
        <CaseStudySection
          title={t('case_study.the_solution')}
          label="// 02 / THE SOLUTION"
        >
          <MarkdownContent content={cs.solution} />
        </CaseStudySection>
      </div>

      {/* Implementation */}
      <ImplementationSection implementation={cs.implementation} />

      {/* Results */}
      <div className="border-t border-zinc-900">
        <CaseStudyResults results={cs.results} />
      </div>

      {/* Before & After */}
      <BeforeAfterSection beforeAfter={cs.beforeAfter} />

      {/* Testimonial */}
      <div className="border-t border-zinc-900">
        <CaseStudyTestimonial testimonial={cs.testimonial} />
      </div>

      {/* Case Navigation */}
      <CaseNavigation currentSlug={slug} />

      {/* CTA */}
      <section className="border-t border-zinc-900 bg-black" aria-labelledby="casestudy-cta-heading">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-6" aria-hidden="true">
            // READY TO GET SIMILAR RESULTS?
          </div>
          <h2 id="casestudy-cta-heading" className="font-display text-3xl md:text-5xl uppercase leading-[0.95] max-w-2xl mx-auto mb-8">
            Start your own<br />
            <span className="text-amber-500">success story.</span>
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/pricing"
              className="inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-400 text-black px-8 py-4 font-bold uppercase tracking-wider text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              View Plans <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </a>
            <a
              href="/cases"
              className="inline-flex items-center gap-3 border border-zinc-700 hover:border-zinc-500 text-white px-8 py-4 font-bold uppercase tracking-wider text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              View All Cases
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
